import React, { useState, useRef, useEffect } from "react";
import classNames from "classnames";
import {
  ChevronUpDownIcon,
  CheckIcon,
  CommandLineIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import { useLayoutContext, AppVersion } from "../core/context/layout-context";

interface VersionSwitcherProps {
  openAside?: boolean;
  isMobile?: boolean;
}

const VERSIONS: {
  id: AppVersion;
  name: string;
  badge: string;
  badgeColor: string;
  description: string;
  modulesIncluded?: string;
  hiddenCount?: number;
}[] = [
  {
    id: "TCCE-V3",
    name: "TCCE-V3",
    badge: "V3.0",
    badgeColor: "bg-emerald-100 text-emerald-800 border-emerald-200",
    description: "Version ciblée : uniquement Expéditions, Réservations, Campagnes et Finances",
    modulesIncluded: "Expéditions · Réservations · Campagnes · Finances",
  },
  {
    id: "TCCE-V2",
    name: "TCCE-V2",
    badge: "V2.0",
    badgeColor: "bg-purple-100 text-purple-800 border-purple-200",
    description: "Version épurée : Dashboards, CRM, BI, Finance et Réservations masqués",
    hiddenCount: 5,
  },
  {
    id: "CRM MOPO-V1.0",
    name: "CRM MOPO-V1.0",
    badge: "V1.0",
    badgeColor: "bg-blue-100 text-blue-800 border-blue-200",
    description: "Version complète avec tous les modules actifs",
  },
];

export default function VersionSwitcher({
  openAside = true,
  isMobile = false,
}: VersionSwitcherProps) {
  const { appVersion, setAppVersion } = useLayoutContext();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const isV3 = appVersion === "TCCE-V3";
  const isV2 = appVersion === "TCCE-V2";
  const isV1 = appVersion === "CRM MOPO-V1.0";

  const handleSelect = (version: AppVersion) => {
    setAppVersion(version);
    setDropdownOpen(false);
  };

  const getThemeColors = () => {
    if (isV3) {
      return {
        bg: "bg-gradient-to-br from-emerald-50/70 to-white border-emerald-200",
        btn: "bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100",
        tag: "bg-emerald-100 text-emerald-700 border-emerald-200",
        dot: "bg-emerald-600",
        activeItem: "bg-emerald-50/80 border-emerald-300 text-emerald-950 font-medium",
        check: "text-emerald-600",
      };
    }
    if (isV2) {
      return {
        bg: "bg-gradient-to-br from-purple-50/70 to-white border-purple-200",
        btn: "bg-purple-50 text-purple-700 border-purple-300 hover:bg-purple-100",
        tag: "bg-purple-100 text-purple-700 border-purple-200",
        dot: "bg-purple-600",
        activeItem: "bg-purple-50/80 border-purple-300 text-purple-950 font-medium",
        check: "text-purple-600",
      };
    }
    return {
      bg: "bg-gradient-to-br from-blue-50/70 to-white border-blue-200",
      btn: "bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100",
      tag: "bg-blue-100 text-blue-700 border-blue-200",
      dot: "bg-blue-600",
      activeItem: "bg-blue-50/80 border-blue-300 text-blue-950 font-medium",
      check: "text-blue-600",
    };
  };

  const currentTheme = getThemeColors();

  // When aside is closed (icon mode)
  if (!openAside && !isMobile) {
    return (
      <div className="relative my-2 flex justify-center" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setDropdownOpen((prev) => !prev)}
          title={`Version active: ${appVersion}. Cliquez pour changer.`}
          className={classNames(
            "h-10 w-10 flex flex-col items-center justify-center rounded-lg border text-xs font-bold transition-all shadow-sm cursor-pointer",
            currentTheme.btn
          )}
        >
          <span className="text-[10px] uppercase tracking-wider font-mono">
            {isV3 ? "V3" : isV2 ? "V2" : "V1"}
          </span>
          <span className="h-1.5 w-1.5 rounded-full mt-0.5 bg-current" />
        </button>

        {dropdownOpen && (
          <div className="absolute left-14 bottom-0 z-50 w-72 rounded-xl bg-white p-3 shadow-xl ring-1 ring-black/10 border border-gray-100">
            <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-100">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Choisir la version
              </span>
              <span
                className={classNames(
                  "text-[11px] px-2 py-0.5 rounded-full font-medium border",
                  currentTheme.tag
                )}
              >
                Actif: {appVersion}
              </span>
            </div>
            <div className="space-y-1.5">
              {VERSIONS.map((ver) => {
                const active = appVersion === ver.id;
                return (
                  <button
                    key={ver.id}
                    type="button"
                    onClick={() => handleSelect(ver.id)}
                    className={classNames(
                      "w-full text-left p-2.5 rounded-lg text-xs transition-colors flex items-start justify-between gap-2 border cursor-pointer",
                      active
                        ? currentTheme.activeItem
                        : "border-transparent text-gray-700 hover:bg-gray-50"
                    )}
                  >
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-sm">{ver.name}</span>
                        {ver.badge && (
                          <span
                            className={classNames(
                              "text-[10px] px-1.5 py-0.2 rounded border font-bold",
                              ver.badgeColor
                            )}
                          >
                            {ver.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                        {ver.description}
                      </p>
                    </div>
                    {active && (
                      <CheckIcon className={classNames("h-4 w-4 shrink-0 mt-0.5", currentTheme.check)} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Expanded mode or mobile mode
  return (
    <div
      ref={dropdownRef}
      className={classNames(
        "relative mx-2 my-2.5 rounded-xl border p-2.5 transition-all shadow-sm",
        currentTheme.bg
      )}
    >
      {/* Top row: Label + Version Quick Pills */}
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5">
          <CommandLineIcon
            className={classNames(
              "h-4 w-4",
              isV3 ? "text-emerald-600" : isV2 ? "text-purple-600" : "text-blue-600"
            )}
          />
          <span className="text-[11px] font-semibold tracking-wider uppercase text-gray-500">
            Version
          </span>
        </div>

        {/* 3 Quick-access Pills */}
        <div className="flex items-center gap-1 bg-white/80 p-0.5 rounded-lg border border-gray-200">
          {(["CRM MOPO-V1.0", "TCCE-V2", "TCCE-V3"] as AppVersion[]).map((v) => {
            const active = appVersion === v;
            const shortName = v === "CRM MOPO-V1.0" ? "V1" : v === "TCCE-V2" ? "V2" : "V3";
            return (
              <button
                key={v}
                type="button"
                onClick={() => setAppVersion(v)}
                title={`Basculer directement vers ${v}`}
                className={classNames(
                  "px-1.5 py-0.5 rounded text-[10px] font-bold transition-all cursor-pointer",
                  active
                    ? v === "TCCE-V3"
                      ? "bg-emerald-600 text-white shadow-2xs"
                      : v === "TCCE-V2"
                      ? "bg-purple-600 text-white shadow-2xs"
                      : "bg-blue-600 text-white shadow-2xs"
                    : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                )}
              >
                {shortName}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main clickable Dropdown trigger */}
      <button
        type="button"
        onClick={() => setDropdownOpen((prev) => !prev)}
        className="w-full flex items-center justify-between rounded-lg bg-white px-2.5 py-1.5 text-left border border-gray-200 hover:border-gray-300 transition-colors shadow-xs group cursor-pointer"
      >
        <div className="flex items-center gap-2 overflow-hidden">
          <span
            className={classNames(
              "h-2 w-2 rounded-full shrink-0",
              currentTheme.dot
            )}
          />
          <div className="truncate">
            <div className="text-xs font-bold text-gray-900 truncate">
              {appVersion}
            </div>
            <div className="text-[10px] text-gray-500 truncate">
              {isV3
                ? "4 modules actifs"
                : isV2
                ? "Mode TCCE (5 masqués)"
                : "Mode complet (8 modules)"}
            </div>
          </div>
        </div>
        <ChevronUpDownIcon className="h-4 w-4 text-gray-400 group-hover:text-gray-600 shrink-0 ml-1" />
      </button>

      {/* Dropdown Menu */}
      {dropdownOpen && (
        <div className="absolute left-0 right-0 bottom-full mb-1.5 z-50 rounded-xl bg-white p-2 shadow-2xl ring-1 ring-black/10 border border-gray-100">
          <div className="px-2 py-1 mb-1 border-b border-gray-100 flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
              Changer de version
            </span>
            <span className="text-[10px] text-gray-400 font-medium">Menu déroulant</span>
          </div>

          <div className="space-y-1">
            {VERSIONS.map((ver) => {
              const active = appVersion === ver.id;
              return (
                <button
                  key={ver.id}
                  type="button"
                  onClick={() => handleSelect(ver.id)}
                  className={classNames(
                    "w-full text-left p-2 rounded-lg text-xs transition-all flex items-start justify-between gap-2 border cursor-pointer",
                    active
                      ? ver.id === "TCCE-V3"
                        ? "bg-emerald-50/90 border-emerald-300 text-emerald-950 font-medium"
                        : ver.id === "TCCE-V2"
                        ? "bg-purple-50/90 border-purple-300 text-purple-950 font-medium"
                        : "bg-blue-50/90 border-blue-300 text-blue-950 font-medium"
                      : "border-transparent text-gray-700 hover:bg-gray-50"
                  )}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-bold text-xs">{ver.name}</span>
                      <span
                        className={classNames(
                          "text-[9px] px-1.5 py-0.5 rounded font-bold border",
                          ver.badgeColor
                        )}
                      >
                        {ver.badge}
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-500 mt-0.5 leading-snug">
                      {ver.description}
                    </p>
                    {ver.modulesIncluded && (
                      <p className="text-[10px] font-semibold text-emerald-700 mt-0.5">
                        {ver.modulesIncluded}
                      </p>
                    )}
                  </div>
                  {active && (
                    <CheckIcon
                      className={classNames(
                        "h-4 w-4 shrink-0 mt-0.5",
                        ver.id === "TCCE-V3"
                          ? "text-emerald-600"
                          : ver.id === "TCCE-V2"
                          ? "text-purple-600"
                          : "text-blue-600"
                      )}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* Helper panel */}
          {isV3 && (
            <div className="mt-2 pt-2 border-t border-gray-100 px-2 py-1.5 bg-emerald-50/60 rounded text-[10px] text-emerald-900">
              <div className="font-bold text-emerald-800 mb-0.5">
                Modules actifs en TCCE-V3 :
              </div>
              <ol className="list-decimal pl-3.5 space-y-0.5 font-medium text-emerald-950">
                <li>Expéditions (Tracking, Envois, etc.)</li>
                <li>Réservations (Moteur de recherche, etc.)</li>
                <li>Campagnes (Leads, Suivi, etc.)</li>
                <li>Finances (Réconciliation, Transactions, États)</li>
              </ol>
            </div>
          )}

          {isV2 && (
            <div className="mt-2 pt-2 border-t border-gray-100 px-2 py-1 bg-gray-50 rounded text-[10px] text-gray-600">
              <div className="font-semibold text-purple-700 flex items-center gap-1 mb-0.5">
                <EyeSlashIcon className="h-3 w-3" />
                Éléments masqués en TCCE-V2 :
              </div>
              <ul className="grid grid-cols-2 gap-x-1 gap-y-0.5 text-gray-500 list-disc pl-3">
                <li>Dashboards</li>
                <li>CRM Client</li>
                <li>BI Data</li>
                <li>Finance</li>
                <li>Réservations</li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
